import React, { useContext, useEffect, useRef, useState } from "react";

import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import { ListItemIcon, makeStyles } from "@material-ui/core";
import {
	Delete,
	Done,
	Edit,
	Event,
	HourglassEmpty,
	Markunread,
	SwapHoriz,
	Undo,
} from "@material-ui/icons";
import {
	KeyboardDateTimePicker,
	MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import "moment/locale/es";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContextIndexedDB";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

moment.locale("es");

const useStyles = makeStyles((theme) => ({
	postponeSubItem: {
		paddingLeft: theme.spacing(4),
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		minWidth: 260,
		fontSize: 14,
	},
	postponeSubLabel: {
		color: theme.palette.text.primary,
	},
	postponeSubDate: {
		color: theme.palette.text.secondary,
		marginLeft: theme.spacing(3),
		fontSize: 13,
		whiteSpace: "nowrap",
	},
	postponeCustomItem: {
		paddingLeft: theme.spacing(4),
		minWidth: 260,
	},
}));

const getPostponeAt8am = (daysToAdd) =>
	moment().add(daysToAdd, "days").hour(8).minute(0).second(0).millisecond(0);

const getNextWeekMondayAt8am = () =>
	moment().isoWeekday(1).add(1, "week").hour(8).minute(0).second(0).millisecond(0);

const formatPostponePreview = (date) =>
	moment(date).format("ddd, HH:mm").replace(/\.$/, "");

const buildPostponeOptions = () => {
	const tomorrow = getPostponeAt8am(1);
	const inTwoDays = getPostponeAt8am(2);
	const inThreeDays = getPostponeAt8am(3);
	const nextWeek = getNextWeekMondayAt8am();

	return [
		{ key: "tomorrow", label: "Mañana", date: tomorrow },
		{ key: "in2days", label: "En 2 días", date: inTwoDays },
		{ key: "in3days", label: "En 3 días", date: inThreeDays },
		{ key: "nextWeek", label: "La próxima semana", date: nextWeek },
	];
};

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl, handleDrawerOpen, onResolveTicket, hideEditAndDelete = false, markAsUnread }) => {
	const classes = useStyles();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [postponeExpanded, setPostponeExpanded] = useState(false);
	const [customPostponeOpen, setCustomPostponeOpen] = useState(false);
	const [customPostponeDate, setCustomPostponeDate] = useState(moment().add(1, "hour"));
	const [postponing, setPostponing] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const { updateTicketInList, setCurrentTicket } = useContext(TicketsContext);
	const history = useHistory();

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!menuOpen) {
			setPostponeExpanded(false);
		}
	}, [menuOpen]);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
			history.replace('/tickets');
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};
	const handleEditContact = () => {
		handleDrawerOpen();
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleMarkAsUnread = () => {
		if (markAsUnread) {
			markAsUnread(ticket.id);
		}
		handleClose();
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleResolveTicket = (e) => {
		if (onResolveTicket) {
			onResolveTicket(e);
		}
		handleClose();
	};

	const handleTogglePostpone = (e) => {
		e.stopPropagation();
		setPostponeExpanded((prev) => !prev);
	};

	const hasActivePostpone = Boolean(ticket?.postponeUntil);

	const handlePostpone = async (postponeUntil) => {
		if (!ticket?.id || postponing) return;

		setPostponing(true);
		const postponeUntilIso = moment(postponeUntil).toISOString();
		try {
			const { data } = await api.post("/chat-postpones", {
				ticketId: Number(ticket.id),
				postponeUntil: postponeUntilIso,
			});
			updateTicketInList({
				...ticket,
				postponeUntil: data?.postponeUntil || postponeUntilIso,
			});
			toast.success(
				`Chat aplazado hasta ${moment(postponeUntil).format("DD/MM/YYYY HH:mm")}`
			);
			handleClose();
			setCustomPostponeOpen(false);
		} catch (err) {
			toastError(err);
		} finally {
			if (isMounted.current) {
				setPostponing(false);
			}
		}
	};

	const handleCancelPostpone = async () => {
		if (!ticket?.id || postponing) return;

		setPostponing(true);
		try {
			const { data } = await api.get("/chat-postpones", {
				params: { ticketId: Number(ticket.id) },
			});
			const postpones = Array.isArray(data?.data)
				? data.data
				: Array.isArray(data)
					? data
					: [];
			// Solo puede haber un pending a la vez; ignorar cancelled/ejecutados.
			const activePostpone =
				postpones.find((item) => item.status === "pending") ||
				[...postpones]
					.filter((item) => item.status !== "cancelled" && !item.sentAt)
					.sort((a, b) => Number(b.id) - Number(a.id))[0];

			if (!activePostpone?.id) {
				updateTicketInList({
					...ticket,
					postponeUntil: null,
				});
				toast.info("No había un aplazamiento activo");
				handleClose();
				return;
			}

			await api.delete(`/chat-postpones/${activePostpone.id}`);
			updateTicketInList({
				...ticket,
				postponeUntil: null,
			});
			toast.success("Aplazamiento cancelado");
			handleClose();
		} catch (err) {
			toastError(err);
		} finally {
			if (isMounted.current) {
				setPostponing(false);
			}
		}
	};

	const handleOpenCustomPostpone = () => {
		setCustomPostponeDate(moment().add(1, "hour").seconds(0).milliseconds(0));
		setCustomPostponeOpen(true);
		handleClose();
	};

	const handleConfirmCustomPostpone = () => {
		if (!customPostponeDate || !moment(customPostponeDate).isValid()) {
			toast.error("Selecciona una fecha y hora válida");
			return;
		}
		if (moment(customPostponeDate).isBefore(moment())) {
			toast.error("La fecha debe ser posterior a la actual");
			return;
		}
		handlePostpone(customPostponeDate);
	};

	const handleReturnTicket = async (e) => {
		// Guardar estado original para rollback
		const originalTicket = { ...ticket };
		
		try {
			console.log(`🚀 UI Optimista - Rechazando ticket ${ticket.id} desde menú...`);
			
			// ========== ACTUALIZACIÓN OPTIMISTA INMEDIATA ==========
			// 1. Remover el ticket de la lista actual (simula que se movió a pending)
			updateTicketInList({
				...ticket,
				status: "pending",
				userId: null,
				updatedAt: new Date().toISOString()
			});
			
			// 2. Remover el ticket actual inmediatamente
			setCurrentTicket({ id: null, code: null });
			
			// 3. Mostrar feedback inmediato
			toast.success("Chat devuelto a pendientes.");
			
			// 4. Navegar de vuelta a la lista
			history.push("/tickets");
			
			// ========== CONFIRMACIÓN CON BACKEND ==========
			console.log(`📡 Confirmando rechazo con backend...`);
			const { data } = await api.put(`/tickets/${ticket.id}`, {
				status: "pending",
				userId: null,
			});
			
			console.log(`✅ Ticket ${ticket.id} confirmado rechazado en backend:`, data);
			
			// 5. Actualizar con datos reales del backend
			updateTicketInList(data);
			
		} catch (err) {
			console.error(`❌ Error rechazando ticket ${ticket.id}:`, err);
			
			// ========== ROLLBACK EN CASO DE ERROR ==========
			// Restaurar el ticket original en la lista
			updateTicketInList(originalTicket);
			
			// Restaurar como ticket actual si era el que estaba abierto
			setCurrentTicket({ id: originalTicket.id, uuid: originalTicket.uuid });
			
			toastError(err);
			toast.error("Error al devolver el chat a pendientes. Se ha revertido la acción.");
		}
		handleClose();
	};

	const postponeOptions = buildPostponeOptions();

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				<MenuItem onClick={handleOpenTransferModal}>
					<ListItemIcon style={{ minWidth: 34 }}>
						<SwapHoriz />
					</ListItemIcon>
					{i18n.t("ticketOptionsMenu.transfer")}
				</MenuItem>
				
				<MenuItem onClick={handleMarkAsUnread}>
					<ListItemIcon style={{ minWidth: 34 }}>
						<Markunread />
					</ListItemIcon>
					{i18n.t("ticketOptionsMenu.markAsUnread")}
				</MenuItem>

				{ticket.status === "open" && (
					<MenuItem onClick={handleReturnTicket}>
						<ListItemIcon style={{ minWidth: 34 }}>
							<Undo />
						</ListItemIcon>
						{i18n.t("messagesList.header.buttons.return")}
					</MenuItem>
				)}

				{ticket.status === "open" && onResolveTicket && (
					<MenuItem onClick={handleResolveTicket}>
						<ListItemIcon style={{ minWidth: 34 }}>
							<Done />
						</ListItemIcon>
						Resolver
					</MenuItem>
				)}

				{ticket.status === "open" && hasActivePostpone && (
					<MenuItem onClick={handleCancelPostpone} disabled={postponing}>
						<ListItemIcon style={{ minWidth: 34 }}>
							<HourglassEmpty />
						</ListItemIcon>
						Cancelar aplazar
					</MenuItem>
				)}

				{ticket.status === "open" && !hasActivePostpone && [
					<MenuItem
						key="postpone"
						onClick={handleTogglePostpone}
						disabled={postponing}
					>
						<ListItemIcon style={{ minWidth: 34 }}>
							<HourglassEmpty />
						</ListItemIcon>
						Aplazar
					</MenuItem>,
					...(postponeExpanded
						? postponeOptions.map((option) => (
							<MenuItem
								key={option.key}
								className={classes.postponeSubItem}
								disabled={postponing}
								onClick={() => handlePostpone(option.date)}
							>
								<span className={classes.postponeSubLabel}>{option.label}</span>
								<span className={classes.postponeSubDate}>
									{formatPostponePreview(option.date)}
								</span>
							</MenuItem>
						))
						: []),
					...(postponeExpanded
						? [
							<MenuItem
								key="postpone-custom"
								className={classes.postponeCustomItem}
								disabled={postponing}
								onClick={handleOpenCustomPostpone}
							>
								<ListItemIcon style={{ minWidth: 34 }}>
									<Event />
								</ListItemIcon>
								Personalizar
							</MenuItem>,
						]
						: []),
				]}
				
				{!hideEditAndDelete && (
					<MenuItem onClick={handleEditContact}>
						<ListItemIcon style={{ minWidth: 34 }}>
							<Edit />
						</ListItemIcon>
						{i18n.t("ticketOptionsMenu.edit")}
					</MenuItem>
				)}
				{!hideEditAndDelete && (
					<Can
						role={user.profile}
						perform="ticket-options:deleteTicket"
						yes={() => (
							<MenuItem onClick={handleOpenConfirmationModal}>
								<ListItemIcon style={{ minWidth: 34 }}>
									<Delete />
								</ListItemIcon>
								{i18n.t("ticketOptionsMenu.delete")}
							</MenuItem>
						)}
					/>
				)}
			</Menu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					ticket.contact?.name
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModalCustom
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
			/>
			<Dialog
				open={customPostponeOpen}
				onClose={() => !postponing && setCustomPostponeOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Personalizar</DialogTitle>
				<DialogContent>
					<MuiPickersUtilsProvider utils={MomentUtils} locale="es">
						<KeyboardDateTimePicker
							variant="inline"
							format="DD/MM/YYYY HH:mm"
							margin="normal"
							fullWidth
							label="Aplazar hasta"
							value={customPostponeDate}
							onChange={(date) => {
								if (date) {
									setCustomPostponeDate(moment(date));
								}
							}}
							KeyboardButtonProps={{
								"aria-label": "change date and time",
							}}
							minDate={moment()}
							minDateMessage="La fecha no puede ser anterior a la actual"
							ampm={false}
							disabled={postponing}
						/>
					</MuiPickersUtilsProvider>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setCustomPostponeOpen(false)}
						color="secondary"
						disabled={postponing}
					>
						Cancelar
					</Button>
					<Button
						onClick={handleConfirmCustomPostpone}
						color="primary"
						variant="contained"
						disabled={postponing}
					>
						{postponing ? "Guardando..." : "Aplazar"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default TicketOptionsMenu;
